// File: /components/Shufflemaps/Dropdowns/NavbarDropdown/NavbarDropdown.jsx
"use client";

import React, { useEffect, useRef, useState, useId, useCallback } from "react";
import styles from "./NavbarDropdown.module.css";

/**
 * NavbarDropdown
 * - Reusable dropdown used in the navbar.
 *
 * Props:
 *   label: string                 // button label
 *   items: {label, href}[]        // menu items
 *   className?: string
 *   showCaret?: boolean           // ← NEW: defaults to true. Set false to hide caret.
 */
export default function NavbarDropdown({
  label = "Main",
  items = [],
  className = "",
  showCaret = true,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) close();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [close]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      btnRef.current?.focus();
    } else if ((e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") && !open) {
      e.preventDefault();
      setOpen(true);
      requestAnimationFrame(() => {
        const first = menuRef.current?.querySelector("a,button,[tabindex='0']");
        first?.focus();
      });
    }
  };

  return (
    <div ref={wrapRef} className={`${styles.dropdown} ${className || ""}`}>
      <button
        ref={btnRef}
        type="button"
        className={styles.dropdownToggle}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        aria-controls={menuId}
        onClick={toggle}
        onKeyDown={onKeyDown}
      >
        {/* Caret is optional now */}
        {showCaret && <span className={styles.dropdownCaret} aria-hidden="true" />}
        <span className={styles.fixedText}>{label}</span>
      </button>

      <ul
        id={menuId}
        ref={menuRef}
        role="menu"
        className={`${styles.dropdownMenu} ${open ? styles.showDropdown : styles.hideDropdown}`}
      >
        {items.map((it, idx) => (
          <li role="none" key={`${it.href}-${idx}`}>
            <a role="menuitem" className={styles.dropdownItem} href={it.href} onClick={close}>
              {it.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
